import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'
import { useMemo } from 'react'
import { usePermissions } from 'lib/rbac'
import { NEW_ROOT_ENTITY_PREFIX } from 'Cube/domain/cube.constants'
import { DisabledReasonType } from 'Cube/domain/cube.domain.types'
import { useCustomer } from '../hooks/useCustomer'
import { getGoCardlessDisabledReason } from 'Integrations/index'
import { getBillingAddress } from '@sequencehq/api/utils/commonModels'

export const useBillingScheduleInspector = () => {
  const ctx = useCubeContext()
  const { updateData } = ctx.mutators
  const { can } = usePermissions()
  const canManageBilling = can('org.billing:update')

  const stripe = useMemo(() => {
    const conflictingProviderActive =
      ctx.queries.availableFeatures.schedule.stripePayment.reasons.some(
        r => r.reasonType === DisabledReasonType.ConflictingPaymentProvider
      )

    return {
      available:
        ctx.queries.availableFeatures.schedule.stripePayment.available
          .enabled && canManageBilling,
      conflictingProviderActive,
      data: {
        acceptPaymentsViaStripe: {
          value: ctx.queries.rawData.data.schedule.stripePayment,
          disabled:
            !ctx.queries.availableFeatures.schedule.stripePayment.available
              .enabled || !canManageBilling
        },
        autoChargeViaStripe: {
          value: ctx.queries.rawData.data.schedule.stripeAutoCharge,
          disabled:
            !ctx.queries.availableFeatures.schedule.stripeAutoCharge.available
              .enabled || !canManageBilling
        }
      },
      onChange: (newData: {
        acceptPaymentsViaStripe?: {
          value: boolean
          disabled?: boolean
        }
        autoChargeViaStripe?: { value: boolean; disabled?: boolean }
      }) => {
        if (
          newData.acceptPaymentsViaStripe !== undefined &&
          newData.acceptPaymentsViaStripe.value !==
            ctx.queries.rawData.data.schedule.stripePayment
        ) {
          updateData({
            schedule: {
              stripePayment: newData.acceptPaymentsViaStripe?.value
            }
          })
        }

        if (
          newData.autoChargeViaStripe !== undefined &&
          newData.autoChargeViaStripe.value !==
            ctx.queries.rawData.data.schedule.stripeAutoCharge
        ) {
          updateData({
            schedule: {
              stripeAutoCharge: newData.autoChargeViaStripe?.value
            }
          })
        }
      }
    }
  }, [
    canManageBilling,
    ctx.queries.availableFeatures.schedule.stripePayment.available,
    ctx.queries.availableFeatures.schedule.stripePayment.reasons,
    ctx.queries.availableFeatures.schedule.stripeAutoCharge.available,
    ctx.queries.rawData.data.schedule.stripePayment,
    ctx.queries.rawData.data.schedule.stripeAutoCharge,
    updateData
  ])

  const { customer } = useCustomer()
  const customerCountry = getBillingAddress(customer?.addresses)?.country

  const goCardless = useMemo(() => {
    const hasCustomer = Boolean(
      ctx.queries.rawData.data.customers[
        ctx.queries.rawData.data.common.customerId
      ]
    )
    const hasProducts = Object.values(ctx.queries.resolvedPhases).some(
      phase => phase.prices.length > 0
    )

    const conflictingProviderActive =
      ctx.queries.availableFeatures.schedule.goCardlessPayment.reasons.some(
        r => r.reasonType === DisabledReasonType.ConflictingPaymentProvider
      )

    const disabledReason = getGoCardlessDisabledReason({
      otherProviderActive: conflictingProviderActive,
      currency: ctx.queries.selectedCurrency,
      country: customerCountry,
      hasCustomer,
      hasProducts
    })

    const available =
      ctx.queries.availableFeatures.schedule.goCardlessPayment.available
        .enabled &&
      canManageBilling &&
      !disabledReason

    return {
      disabled: !available,
      disabledReason,
      data: {
        acceptPaymentsViaGoCardless: {
          value: ctx.queries.rawData.data.schedule.goCardlessPayment,
          disabled:
            !ctx.queries.availableFeatures.schedule.goCardlessPayment.available
              .enabled || !canManageBilling
        }
      },
      onChange: (newData: {
        acceptPaymentsViaGoCardless?: {
          value: boolean
          disabled?: boolean
        }
      }) => {
        if (
          newData.acceptPaymentsViaGoCardless !== undefined &&
          newData.acceptPaymentsViaGoCardless.value !==
            ctx.queries.rawData.data.schedule.goCardlessPayment
        ) {
          updateData({
            schedule: {
              goCardlessPayment: newData.acceptPaymentsViaGoCardless?.value
            }
          })
        }
      }
    }
  }, [
    canManageBilling,
    ctx.queries.availableFeatures.schedule.goCardlessPayment.available,
    ctx.queries.availableFeatures.schedule.goCardlessPayment.reasons,
    ctx.queries.rawData.data.schedule.goCardlessPayment,
    ctx.queries.rawData.data.customers,
    ctx.queries.rawData.data.common.customerId,
    ctx.queries.resolvedPhases,
    ctx.queries.selectedCurrency,
    customerCountry,
    updateData
  ])

  return {
    stripe,
    goCardless,
    customerId: ctx.queries.rawData.data.common.customerId,
    billingScheduleId: ctx.queries.rawData.data.common.id,
    billingScheduleCreatedAt: ctx.queries.rawData.data.common.createdAt,
    isPersistedSchedule: !ctx.queries.rawData.data.common.id.startsWith(
      NEW_ROOT_ENTITY_PREFIX
    )
  }
}
